<template>
  <div class="owe-fee-wrapper">
    <div class="owe-fee-header">
      <div class="header-left">
        <span class="room-label">{{ oweFeeInfo.roomName ? ($t('simplifyOweFee.room') + ': ' + oweFeeInfo.roomName) : (oweFeeInfo.carNum || '') }}</span>
        <span v-if="!oweFeeInfo.ownerId" class="warn-tip">{{ $t('simplifyOweFee.noOwnerTip') }}</span>
      </div>
      <div class="header-right">
        <!-- 生成预收票据按钮已隐藏 -->
        <el-button type="warning" size="small" v-if="oweFeeInfo.ownerId || oweFeeInfo.roomId || oweFeeInfo.carId" @click="_openPreReceiptDialog">
          <i class="el-icon-document"></i> 生成预收票据
        </el-button>
        <el-button type="primary" size="small" v-if="oweFeeInfo.carId" @click="_openCarCreateFeeAddModal">
          <i class="el-icon-plus"></i> 创建费用
        </el-button>
        <el-button type="primary" size="small" v-if="oweFeeInfo.roomId && !oweFeeInfo.carId" @click="_openRoomCreateFeeAddModal">
          <i class="el-icon-plus"></i> 创建费用
        </el-button>
        <el-button type="danger" size="small" plain v-if="selectedFees.length > 0" @click="_batchCancelFees">
          <i class="el-icon-delete"></i> 批量取消({{ selectedFees.length }})
        </el-button>
        <el-button type="warning" size="small" plain v-if="selectedFees.length > 0" @click="_openApplyDeduction">
          <i class="el-icon-discount"></i> 申请减免({{ selectedFees.length }})
        </el-button>
        <el-button type="primary" size="small" v-if="oweFeeInfo.ownerId || oweFeeInfo.roomId || oweFeeInfo.carId" @click="_batchPayOweFees">
          <i class="el-icon-money"></i> {{ $t('simplifyOweFee.batchPayment') }}
        </el-button>
      </div>
    </div>

    <!-- 合计栏 -->
    <div class="total-bar" v-if="loaded">
      <span>{{ $t('simplifyOweFee.roomOweTotal') }}：{{ roomTotal.toFixed(2) }}</span>
      <span>{{ $t('simplifyOweFee.carOweTotal') }}：{{ carTotal.toFixed(2) }}</span>
      <span class="grand-total">{{ $t('simplifyOweFee.grandTotal') }}：<b>{{ grandTotal.toFixed(2) }}</b></span>
    </div>

    <!-- 表格滚动区域 -->
    <div class="table-scroll-area" v-if="loaded">

    <!-- 房屋欠费：按费用类型分组 -->
    <div v-for="(group, gIdx) in roomFeeGroups" :key="'room_' + gIdx" class="fee-group">
      <div class="group-title">{{ group.feeName }}（{{ group.fees.length }}条）</div>
      <el-table :data="group.fees" style="width: 100%" border stripe size="mini"
        :row-key="'feeId'"
        @selection-change="(rows) => _onSelectionChange(rows, 'room_' + gIdx)"
        class="owe-fee-table">
        <el-table-column type="selection" width="40" :selectable="_isOweSelectable" />
        <el-table-column prop="feeName" :label="$t('simplifyOweFee.feeItem')" align="center" width="120"></el-table-column>
        <el-table-column label="应收金额" align="center" width="85">
          <template #default="{row}">
            <span>{{ _getReceivableAmount(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('simplifyOweFee.amountOwed')" align="center" width="85">
          <template #default="{row}">
            <span :class="parseFloat(row.amountOwed || 0) > 0 ? 'owe-amount' : (parseFloat(row.amountOwed || 0) < 0 ? 'negative-amount' : '')">
              {{ parseFloat(row.amountOwed || 0) != 0 ? parseFloat(row.amountOwed).toFixed(2) : '-' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="已开票" align="center" width="75">
          <template #default="{row}">
            <span :class="parseFloat(row._invoicedAmount||0) > 0 ? 'invoiced-amount' : 'zero-amount'">{{ parseFloat(row._invoicedAmount||0) > 0 ? parseFloat(row._invoicedAmount).toFixed(2) : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="已收" align="center" width="75">
          <template #default="{row}">
            <span :class="parseFloat(row._receivedAmount||0) > 0 ? 'received-amount' : 'zero-amount'">{{ parseFloat(row._receivedAmount||0) > 0 ? parseFloat(row._receivedAmount).toFixed(2) : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="减免/抵扣" align="center" width="90">
          <template #default="{row}">
            <span :class="(parseFloat(row._deductionAmount||0) + parseFloat(row._acctAmount||0)) > 0 ? 'deduction-amount' : 'zero-amount'">{{ (parseFloat(row._deductionAmount||0) + parseFloat(row._acctAmount||0)) > 0 ? (parseFloat(row._deductionAmount||0) + parseFloat(row._acctAmount||0)).toFixed(2) : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" align="center" width="70">
          <template #default="{row}">
            <el-tag :type="_getOweStatus(row) === '欠费' ? 'danger' : (_getOweStatus(row) === '退费' ? 'warning' : 'success')" size="mini" effect="plain">
              {{ _getOweStatus(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('simplifyOweFee.receivablePeriod')" align="center" min-width="160">
          <template #default="{row}">
            {{ _getPeriod(row) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('simplifyOweFee.operation')" align="center" width="180" fixed="right">
          <template #default="{row}">
            <el-button type="text" size="small" @click="_paySingleFee(row)">
              {{ $t('simplifyOweFee.payment') }}
            </el-button>
            <el-button type="text" size="small" @click="_viewHistory(row)">
              {{ $t('simplifyOweFee.history') }}
            </el-button>
            <el-button type="text" size="small" style="color:#F56C6C" @click="_cancelFee(row)">
              取消费用
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 车辆欠费 -->
    <div v-for="(group, idx) in carFeeGroups" :key="idx" class="fee-group" style="margin-top:10px;">
      <div class="group-title group-car">{{ $t('simplifyOweFee.carFee') }} - {{ group.carNum }}</div>
      <el-table :data="group.fees" style="width: 100%" border stripe size="mini"
        :row-key="'feeId'"
        @selection-change="(rows) => _onSelectionChange(rows, 'car_' + idx)"
        class="owe-fee-table">
        <el-table-column type="selection" width="40" :selectable="_isOweSelectable" />
        <el-table-column prop="feeName" :label="$t('simplifyOweFee.feeItem')" align="center" width="120"></el-table-column>
        <el-table-column label="应收金额" align="center" width="85">
          <template #default="{row}">
            <span>{{ _getReceivableAmount(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('simplifyOweFee.amountOwed')" align="center" width="85">
          <template #default="{row}">
            <span :class="parseFloat(row.amountOwed || 0) > 0 ? 'owe-amount' : (parseFloat(row.amountOwed || 0) < 0 ? 'negative-amount' : '')">
              {{ parseFloat(row.amountOwed || 0) != 0 ? parseFloat(row.amountOwed).toFixed(2) : '-' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="已开票" align="center" width="75">
          <template #default="{row}">
            <span :class="parseFloat(row._invoicedAmount||0) > 0 ? 'invoiced-amount' : 'zero-amount'">{{ parseFloat(row._invoicedAmount||0) > 0 ? parseFloat(row._invoicedAmount).toFixed(2) : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="已收" align="center" width="75">
          <template #default="{row}">
            <span :class="parseFloat(row._receivedAmount||0) > 0 ? 'received-amount' : 'zero-amount'">{{ parseFloat(row._receivedAmount||0) > 0 ? parseFloat(row._receivedAmount).toFixed(2) : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="减免/抵扣" align="center" width="90">
          <template #default="{row}">
            <span :class="(parseFloat(row._deductionAmount||0) + parseFloat(row._acctAmount||0)) > 0 ? 'deduction-amount' : 'zero-amount'">{{ (parseFloat(row._deductionAmount||0) + parseFloat(row._acctAmount||0)) > 0 ? (parseFloat(row._deductionAmount||0) + parseFloat(row._acctAmount||0)).toFixed(2) : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" align="center" width="70">
          <template #default="{row}">
            <el-tag :type="_getOweStatus(row) === '欠费' ? 'danger' : (_getOweStatus(row) === '退费' ? 'warning' : 'success')" size="mini" effect="plain">
              {{ _getOweStatus(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('simplifyOweFee.receivablePeriod')" align="center" min-width="160">
          <template #default="{row}">
            {{ _getPeriod(row) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('simplifyOweFee.operation')" align="center" width="180" fixed="right">
          <template #default="{row}">
            <el-button type="text" size="small" @click="_paySingleFee(row)">
              {{ $t('simplifyOweFee.payment') }}
            </el-button>
            <el-button type="text" size="small" style="color:#F56C6C" @click="_cancelFee(row)">
              取消费用
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 无数据提示 -->
    <div v-if="roomFees.length === 0 && carFeeGroups.length === 0" class="no-data-tip">
      <i class="el-icon-check"></i> 暂无费用记录
    </div>

    </div>
    <!-- 加载中 -->
    <div v-if="!loaded" class="loading-tip">
      <i class="el-icon-loading"></i> {{ $t('simplifyOweFee.loading') }}
    </div>

    <pre-receipt-dialog ref="preReceiptDialog" />
    <delete-fee ref="deleteFee" @success="_onCancelFeeSuccess" />
    <room-create-fee-add ref="roomCreateFeeAdd" @success="_onCreateFeeSuccess" />
    <car-create-fee-add ref="carCreateFeeAdd" @success="_onCreateFeeSuccess" />
    <apply-fee-deduction ref="applyFeeDeduction" @success="_onApplyDeductionSuccess" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listFee as listCarFee, queryOwnerCars } from '@/api/simplify/simplifyCarFeeApi'
import { listFee as listRoomFee } from '@/api/fee/simplifyRoomFeeApi'
import { listFeeObj } from '@/api/fee/batchPayFeeOrderApi'
import { listFeeAmountSummary } from '@/api/fee/feeAmountSummaryApi'
import { deleteFee as deleteFeeApi } from '@/api/fee/deleteFeeApi'
import { dateFormat } from '@/utils/dateUtil'
import PreReceiptDialog from '@/components/fee/preReceiptDialog'
import DeleteFee from '@/components/fee/deleteFee'
import RoomCreateFeeAdd from '@/components/fee/roomCreateFeeAdd'
import CarCreateFeeAdd from '@/components/fee/carCreateFeeAdd'
import ApplyFeeDeduction from '@/components/fee/applyFeeDeduction'

export default {
  name: 'SimplifyOweFee',
  components: {
    PreReceiptDialog,
    DeleteFee,
    RoomCreateFeeAdd,
    CarCreateFeeAdd,
    ApplyFeeDeduction
  },
  data() {
    return {
      communityId: '',
      oweFeeInfo: {
        roomId: '',
        roomName: '',
        ownerId: '',
        ownerName: ''
      },
      roomFees: [],
      carFeeGroups: [],
      loaded: false,
      roomTotal: 0,
      carTotal: 0,
      selectedFees: [] // 表格勾选的欠费
    }
  },
  computed: {
    grandTotal() {
      return this.roomTotal + this.carTotal
    },
    // 按费用名称分组并按月份升序排列
    roomFeeGroups() {
      const map = {}
      this.roomFees.forEach(fee => {
        const key = fee.feeName || '其他费用'
        if (!map[key]) map[key] = []
        map[key].push(fee)
      })
      // 每组内按 startTime 升序
      const result = Object.keys(map).map(name => {
        map[name].sort((a, b) => {
          const tA = a.startTime ? new Date(a.startTime).getTime() : 0
          const tB = b.startTime ? new Date(b.startTime).getTime() : 0
          return tA - tB
        })
        return { feeName: name, fees: map[name] }
      })
      return result
    }
  },
  created() {
    this.communityId = getCommunityId()
  },
  methods: {
    open(param) {
      this.handleSwitch(param)
    },

    async handleSwitch(param) {
      this.loaded = false
      this.roomFees = []
      this.carFeeGroups = []
      this.roomTotal = 0
      this.carTotal = 0
      this.selectedFees = []
      Object.assign(this.oweFeeInfo, param)
      // 纯车牌：不需要房屋ID，直接加载车辆欠费
      if (!param.roomId && !param.carId) return

      try {
        await Promise.all([
          this._loadRoomOweFees(),
          this._loadCarOweFees()
        ])
      } catch (e) {
        console.error('Load owe fees error:', e)
      }
      this.loaded = true
    },

    async _loadRoomOweFees() {
      // 车辆页面不加载房屋费用，仅房屋页面展示本房屋费用
      if (!this.oweFeeInfo.roomId || this.oweFeeInfo.carId) return
      const params = {
        page: 1,
        row: 200,
        communityId: this.communityId,
        payerObjId: this.oweFeeInfo.roomId,
        payerObjType: '3333',
        state: '2008001',
        orderBy: 'start_time'
      }
      const res = await listRoomFee(params)
      const fees = res.fees || []
      // 用 feeTotalPrice 补齐空的 receivableAmount
      fees.forEach(f => {
        if (!f.receivableAmount || parseFloat(f.receivableAmount) === 0) {
          f.receivableAmount = f.feeTotalPrice || 0
        }
      })
      // 对于 receivableAmount 仍为 0 的，调 listFeeObj 获取
      await this._fillReceivableAmounts(fees)
      // 批量查询已收/已开票/减免金额（欠费列表展示）
      await this._fillAmountSummary(fees)
      this.roomFees = fees
      let total = 0
      this.roomFees.forEach(f => { total += parseFloat(f.amountOwed || 0) })
      this.roomTotal = total
    },

    async _loadCarOweFees() {
      // 车辆页面：只查当前选中的车辆欠费（不按业主查全部车辆）
      if (this.oweFeeInfo.carId) {
        const feeParams = {
          page: 1,
          row: 200,
          communityId: this.communityId,
          payerObjId: this.oweFeeInfo.carId,
          payerObjType: '6666',
          state: '2008001',
          orderBy: 'start_time'
        }
        const feeRes = await listCarFee(feeParams)
        const fees = ((feeRes.data && feeRes.data.fees) || [])
          .filter(f => f.payerObjType === '6666')
        fees.forEach(f => {
          if (!f.receivableAmount || parseFloat(f.receivableAmount) === 0) {
            f.receivableAmount = f.feeTotalPrice || 0
          }
        })
        await this._fillReceivableAmounts(fees)
        // 批量查询已收/已开票/减免金额
        await this._fillAmountSummary(fees)
        if (fees.length > 0) {
          this.carFeeGroups = [{
            carId: this.oweFeeInfo.carId,
            carNum: this.oweFeeInfo.carNum || '',
            fees: fees
          }]
          let total = 0
          fees.forEach(f => { total += parseFloat(f.amountOwed || 0) })
          this.carTotal = total
        }
        return
      }

      // 房屋页面：只查该房屋绑定的车辆（通过roomId），不按业主查全部车辆
      if (!this.oweFeeInfo.roomId) return
      const params = {
        page: 1,
        row: 50,
        carTypeCd: '1001',
        communityId: this.communityId,
        roomId: this.oweFeeInfo.roomId
      }
      const carRes = await queryOwnerCars(params)
      const cars = (carRes.data && carRes.data.data) || []

      if (!cars || cars.length === 0) return

      const groups = []
      let totalCar = 0
      for (const car of cars) {
        const feeParams = {
          page: 1,
          row: 200,
          communityId: this.communityId,
          payerObjId: car.carId,
          payerObjType: '6666',
          state: '2008001',
          orderBy: 'start_time'
        }
        const feeRes = await listCarFee(feeParams)
        const fees = ((feeRes.data && feeRes.data.fees) || [])
          .filter(f => f.payerObjType === '6666')
        fees.forEach(f => {
          if (!f.receivableAmount || parseFloat(f.receivableAmount) === 0) {
            f.receivableAmount = f.feeTotalPrice || 0
          }
        })
        await this._fillReceivableAmounts(fees)
        // 批量查询已收/已开票/减免金额
        await this._fillAmountSummary(fees)
        if (fees.length > 0) {
          groups.push({
            carId: car.carId,
            carNum: car.carNum,
            fees: fees
          })
          fees.forEach(f => { totalCar += parseFloat(f.amountOwed || 0) })
        }
      }
      this.carFeeGroups = groups
      this.carTotal = totalCar
    },

    _openPreReceiptDialog() {
      // 优先使用用户勾选的欠费，未勾选则使用全部
      let targetFees = this.selectedFees.length > 0 ? this.selectedFees : [
        ...this.roomFees,
        ...this.carFeeGroups.flatMap(g => g.fees)
      ]
      if (targetFees.length === 0) {
        this.$message.warning('当前没有欠费')
        return
      }
      const objType = this.oweFeeInfo.roomId ? '3333' : '6666'
      const objId = this.oweFeeInfo.roomId || this.oweFeeInfo.carId || ''
      const objName = this.oweFeeInfo.roomName || this.oweFeeInfo.carNum || ''
      // 付费对象优先用业主（与正式收据 generator 的 payObjId=业主ID 保持一致），
      // 这样预收票据打印时显示"业主姓名（房号）"，与正式收据一致；
      // 业主信息优先级：oweFeeInfo（父组件传入）> 欠费费用属性 feeAttrs(390007业主ID/390008业主名) > 房屋/车辆兜底
      const feeOwner = this._extractOwnerFromFees(targetFees)
      const payObjId = this.oweFeeInfo.ownerId || (feeOwner && feeOwner.ownerId) || objId
      const payObjName = this.oweFeeInfo.ownerName || (feeOwner && feeOwner.ownerName) || objName
      this.$refs.preReceiptDialog.open({
        objType,
        objId,
        objName,
        payObjId,
        payObjName,
        fees: targetFees
      }).then((result) => {
        if (result) {
          this.$message.success('预收票据已生成，可在应收票据tab查看')
          this.selectedFees = []
          // 触发父组件刷新
          this.$emit('preReceiptCreated', result)
        }
      })
    },
    // 从欠费费用属性的 feeAttrs 中提取业主信息
    // specCd 390007=业主ID, 390008=业主名称；owner_id='-1' 视为无业主
    _extractOwnerFromFees(fees) {
      if (!fees || fees.length === 0) return null
      for (const fee of fees) {
        const attrs = fee.feeAttrs || fee.feeAttrsDtos || []
        let ownerId = ''
        let ownerName = ''
        for (const attr of attrs) {
          if (attr.specCd === '390007') ownerId = attr.value
          if (attr.specCd === '390008') ownerName = attr.value
        }
        if (ownerId && ownerId !== '-1' && ownerName) {
          return { ownerId, ownerName }
        }
      }
      return null
    },
    // 表格多选变化（按表格分组追踪，合并为统一选中列表）
    _onSelectionChange(rows, groupKey) {
      if (!this._groupSelections) this._groupSelections = {}
      // 直接替换该表格分组的选中状态
      this._groupSelections[groupKey] = rows
      // 延迟合并（同一批次多表格事件）
      clearTimeout(this._mergeTimer)
      this._mergeTimer = setTimeout(() => {
        const merged = []
        Object.values(this._groupSelections).forEach(groupRows => {
          groupRows.forEach(f => merged.push(f))
        })
        this.selectedFees = merged
      }, 30)
    },
    // 所有费用行均可勾选
    _isOweSelectable() {
      return true
    },

    _batchPayOweFees() {
      // 车辆页面：按选中的车辆缴费
      if (this.oweFeeInfo.carId) {
        this.$router.push({
          path: '/views/fee/batchPayFeeOrder',
          query: {
            carId: this.oweFeeInfo.carId,
            carNum: this.oweFeeInfo.carNum || '',
            payerObjType: '6666',
            ownerId: this.oweFeeInfo.ownerId || ''
          }
        })
        return
      }
      // 房屋页面：按选中的房屋缴费（批量缴费页会自动加载该房屋绑定的车辆费用）
      if (this.oweFeeInfo.roomId) {
        this.$router.push({
          path: '/views/fee/batchPayFeeOrder',
          query: {
            roomId: this.oweFeeInfo.roomId,
            roomName: this.oweFeeInfo.roomName || '',
            payerObjType: '3333',
            ownerId: this.oweFeeInfo.ownerId || ''
          }
        })
        return
      }
    },

    _paySingleFee(fee) {
      this.$router.push(`/views/fee/payFeeOrder?feeId=${fee.feeId}&ownerId=${this.oweFeeInfo.ownerId || ''}`)
    },

    _viewHistory(fee) {
      this.$router.push({ path: '/views/fee/propertyFee', query: fee })
    },
    _cancelFee(fee) {
      this.$refs.deleteFee.open({
        communityId: this.communityId,
        feeId: fee.feeId
      })
    },
    async _batchCancelFees() {
      if (this.selectedFees.length === 0) {
        this.$message.warning('请先勾选要取消的费用')
        return
      }
      const feeNames = [...new Set(this.selectedFees.map(f => f.feeName || '其他费用'))].join('、')
      try {
        await this.$confirm(
          `确认取消以下费用项目的 ${this.selectedFees.length} 条费用吗？<br/>费用项目：${feeNames}<br/>取消后不可恢复，是否继续？`,
          '批量取消费用',
          { dangerouslyUseHTMLString: true, confirmButtonText: '确认取消', cancelButtonText: '取消', type: 'warning' }
        )
      } catch {
        return
      }
      const loading = this.$loading({ text: '正在批量取消...', background: 'rgba(0,0,0,0.3)' })
      let successCount = 0
      let failCount = 0
      const tasks = this.selectedFees.map(fee =>
        deleteFeeApi({ communityId: this.communityId, feeId: fee.feeId })
          .then(() => { successCount++ })
          .catch(err => { console.error('取消费用失败:', fee.feeId, err); failCount++ })
      )
      await Promise.all(tasks)
      loading.close()
      if (failCount > 0) {
        this.$message.warning(`批量取消完成：成功 ${successCount} 条，失败 ${failCount} 条`)
      } else {
        this.$message.success(`已成功取消 ${successCount} 条费用`)
      }
      this.selectedFees = []
      this._groupSelections = {}
      // 刷新数据
      this.handleSwitch(this.oweFeeInfo)
    },
    _onCancelFeeSuccess() {
      // 取消费用成功后刷新数据
      this.handleSwitch(this.oweFeeInfo)
    },
    _openRoomCreateFeeAddModal() {
      if (!this.oweFeeInfo.roomId) {
        this.$message.warning('请先选择房屋')
        return
      }
      this.$refs.roomCreateFeeAdd.open({
        isMore: false,
        roomId: this.oweFeeInfo.roomId,
        roomName: this.oweFeeInfo.roomName || '',
        ownerName: this.oweFeeInfo.ownerName || ''
      })
    },
    _openCarCreateFeeAddModal() {
      if (!this.oweFeeInfo.carId) {
        this.$message.warning('请先选择车辆')
        return
      }
      this.$refs.carCreateFeeAdd.open({
        isMore: false,
        car: {
          carId: this.oweFeeInfo.carId,
          carNum: this.oweFeeInfo.carNum || ''
        }
      })
    },
    _onCreateFeeSuccess() {
      // 创建费用成功后刷新欠费列表
      this.handleSwitch(this.oweFeeInfo)
    },

    _openApplyDeduction() {
      // 申请减免：使用用户勾选的费用项目（未欠费的也允许，例如对预缴/后续费用减免）
      const targetFees = this.selectedFees
      if (targetFees.length === 0) {
        this.$message.warning('请先勾选费用项目')
        return
      }
      this.$refs.applyFeeDeduction.open({ fees: targetFees })
    },
    _onApplyDeductionSuccess() {
      // 申请提交成功后刷新（欠费暂不变，因未审核）
      this.selectedFees = []
      this._groupSelections = {}
      this.handleSwitch(this.oweFeeInfo)
    },

    _getPeriod(fee) {
      if (fee.state === '2009001') return '-'
      const start = fee.startTime ? dateFormat(fee.startTime).substring(0, 10) : '-'
      let end = fee.endTime ? dateFormat(fee.endTime).substring(0, 10) : '-'
      if (start === end && fee.deadlineTime) {
        end = dateFormat(fee.deadlineTime).substring(0, 10)
      }
      return `${start}~${end}`
    },

    _getOweStatus(fee) {
      const amt = parseFloat(fee.amountOwed || 0)
      if (amt > 0) return '欠费'
      if (amt < 0) return '退费'
      return '未欠费'
    },

    _getReceivableAmount(fee) {
      const amt = parseFloat(fee.receivableAmount || 0)
      if (amt > 0) return amt.toFixed(2)
      const owed = parseFloat(fee.amountOwed || 0)
      if (owed !== 0) return owed.toFixed(2)
      const total = parseFloat(fee.feeTotalPrice || 0)
      if (total > 0) return total.toFixed(2)
      return '-'
    },

    // 调用 listFeeObj 补充应收金额（refer batchPayFeeOrderList）
    async _fillReceivableAmounts(fees) {
      const needFill = fees.filter(f => {
        const ra = parseFloat(f.receivableAmount || 0)
        const tp = parseFloat(f.feeTotalPrice || 0)
        return (ra === 0 && tp === 0) && f.feeId
      })
      if (needFill.length === 0) return
      const concurrency = 6
      for (let i = 0; i < needFill.length; i += concurrency) {
        const batch = needFill.slice(i, i + concurrency)
        await Promise.all(batch.map(async (fee) => {
          try {
            const objRes = await listFeeObj({
              communityId: this.communityId,
              feeId: fee.feeId,
              page: 1,
              row: 1,
              cycle: fee.paymentCycle || 1
            })
            if (objRes.code === 0 && objRes.data && objRes.data.feeTotalPrice) {
              fee.receivableAmount = objRes.data.feeTotalPrice
            }
          } catch (e) {
            console.error('获取费用金额失败:', fee.feeId, e)
          }
        }))
      }
    },

    // 按 feeId 批量查询已收/已开票/减免金额，写回每个 fee（避免 N+1 请求）
    async _fillAmountSummary(fees) {
      if (!fees || fees.length === 0) return
      const feeIds = fees.map(f => f.feeId).filter(id => id)
      if (feeIds.length === 0) return
      try {
        const res = await listFeeAmountSummary({
          communityId: this.communityId,
          feeIds: feeIds.join(',')
        })
        const list = (res && res.data) || res || []
        // 建 feeId → 金额 的映射
        const map = {}
        list.forEach(item => {
          map[item.feeId] = item
        })
        fees.forEach(f => {
          const m = map[f.feeId]
          f._receivedAmount = m ? parseFloat(m.receivedAmount || 0) : 0
          f._invoicedAmount = m ? parseFloat(m.invoicedAmount || 0) : 0
          f._deductionAmount = m ? parseFloat(m.deductionAmount || 0) : 0
          f._acctAmount = m ? parseFloat(m.acctAmount || 0) : 0
          // 重新计算欠费金额：应收 - 已收 - 减免 - 账户抵扣（支持负数，即预缴/多缴场景）
          const receivable = parseFloat(f.receivableAmount || f.feeTotalPrice || 0)
          f.amountOwed = (receivable - f._receivedAmount - f._deductionAmount - f._acctAmount).toFixed(2)
        })
      } catch (e) {
        console.error('获取费用金额汇总失败:', e)
        // 失败不影响主流程，金额默认 0
        fees.forEach(f => {
          if (f._receivedAmount === undefined) f._receivedAmount = 0
          if (f._invoicedAmount === undefined) f._invoicedAmount = 0
          if (f._deductionAmount === undefined) f._deductionAmount = 0
          if (f._acctAmount === undefined) f._acctAmount = 0
        })
      }
    },

  }
}
</script>

<style scoped>
.owe-fee-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 240px);
}

.owe-fee-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.header-left {
  font-size: 14px;
}

.room-label {
  font-weight: bold;
  margin-right: 15px;
}

.warn-tip {
  color: #E6A23C;
  font-size: 12px;
}

.fee-group {
  margin-bottom: 5px;
}

.group-title {
  font-size: 14px;
  font-weight: bold;
  padding: 5px 0;
  color: #303133;
  border-left: 3px solid #409EFF;
  padding-left: 8px;
  background: #f5f7fa;
}

.group-car {
  border-left-color: #67C23A;
}

.owe-amount {
  color: #F56C6C;
  font-weight: bold;
}

.negative-amount {
  color: #409EFF;
  font-weight: bold;
}

/* 资金流向金额样式 */
.invoiced-amount {
  color: #E6A23C;
  font-weight: bold;
}

.received-amount {
  color: #67C23A;
  font-weight: bold;
}

.deduction-amount {
  color: #909399;
  font-weight: bold;
}

.zero-amount {
  color: #C0C4CC;
}

.total-bar {
  display: flex;
  gap: 30px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  flex-shrink: 0;
}

.grand-total {
  margin-left: auto;
  font-size: 14px;
  color: #F56C6C;
}

.table-scroll-area {
  flex: 1;
  overflow-y: auto;
  margin-top: 8px;
}

.subtotal-row {
  text-align: right;
  padding: 4px 12px;
  background: #fdf6ec;
  border: 1px solid #EBEEF5;
  border-top: none;
  font-size: 13px;
  color: #E6A23C;
}

.no-data-tip {
  text-align: center;
  padding: 40px 0;
  color: #909399;
  font-size: 14px;
}

.loading-tip {
  text-align: center;
  padding: 40px 0;
  color: #909399;
  font-size: 14px;
}

/* 紧凑表格行间距 */
.owe-fee-table >>> th,
.owe-fee-table >>> td {
  padding: 3px 0;
}

/* 欠费状态标签 */
.owe-fee-table >>> .el-tag--mini {
  height: 20px;
  line-height: 18px;
}
</style>
